<template>
  <div class="px-7 pb-6 pt-5">
    <div class="flex flex-col md:flex-row gap-8 max-w-[780px]">
      <div class="flex-1">
        <form id="pay-form" @submit="onSubmit">
          <fieldset class="flex flex-col gap-2 mb-8">
            <Label for="billingCycle"> Billing cycle </Label>
            <div>
              <RadioGroupRoot
                v-model="selectedPriceId"
                class="grid grid-cols-2 gap-4"
                :default-value="selectedPriceId"
                aria-label="Billing cycle"
              >
                <label
                  v-for="price in prices"
                  :key="price.id"
                  :for="price.stripe_price_id"
                  class="flex items-center gap-3 border rounded-lg px-6 py-4 cursor-pointer transition duration-200 ease-in-out"
                  :class="{
                    'bg-primary-600 border-accent-400':
                      selectedPriceId === price.stripe_price_id,
                    'bg-primary-700 border-primary-400':
                      selectedPriceId !== price.stripe_price_id,
                  }"
                >
                  <RadioGroupItem
                    :id="price.stripe_price_id"
                    class="hidden bg-neutral-900 size-[20px] rounded-full hover:bg-neutral-800 shadow-[inset_0_0_1px_rgba(0,0,0)] shadow-white focus:shadow-white outline-none"
                    :value="price.stripe_price_id"
                  >
                    <RadioGroupIndicator
                      class="flex items-center justify-center w-full h-full relative after:content-[''] after:block after:size-[12px] after:rounded-[50%] after:bg-accent-400"
                    />
                  </RadioGroupItem>

                  <span class="block font-semibold">
                    <span class="block text-base text-neutral-50">
                      Pay
                      <span v-if="price.yearly_unit_amount">Yearly</span>
                      <span v-else>Monthly</span>
                    </span>
                    <span class="block text-sm text-neutral-500">
                      ${{ price.unit_amount / 100 }} / month / editor
                    </span>
                  </span>
                </label>
              </RadioGroupRoot>
            </div>
          </fieldset>
          <fieldset class="flex flex-col gap-1 mb-8">
            <Label for="cardName"> Name on card </Label>
            <Input
              v-bind="cardNameAttrs"
              id="cardName"
              v-model="cardName"
              placeholder="Card name"
            />
            <span class="text-error-400">{{ errors?.cardName }}</span>
          </fieldset>
          <div
            id="payment-element"
            style="padding: 0; background-color: transparent"
          />
          <fieldset class="flex flex-col gap-1">
            <Label for="country"> Country </Label>
            <select
              v-bind="countryAttrs"
              id="country"
              v-model="country"
              class="h-10 w-full rounded border border-primary-400 bg-primary-700 text-neutral-300 placeholder:text-neutral-500 ring-offset-neutral-900 focus:ring-2 focus:ring-offset-[0px] focus:ring-neutral-300/20 px-3 py-2 text-sm"
            >
              <option value="">Select a country</option>
              <option v-for="(item, key) in countries" :key="key" :value="key">
                {{ item }}
              </option>
            </select>
            <span class="text-error-400">{{ errors?.country }}</span>
          </fieldset>
          <Collapsible v-model:open="isBillingDetailsOpen">
            <CollapsibleTrigger as-child>
              <Button
                type="button"
                variant="ghost"
                class="text-accent-400 border-none text-sm p-0"
              >
                <template #startIcon>
                  <ChevronDown v-if="!isBillingDetailsOpen" class="w-6 h-6" />
                  <ChevronUp v-else class="w-6 h-6" />
                </template>
                {{ isBillingDetailsOpen ? 'Hide' : 'Show' }} Billing Details
                (Optional)
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent class="space-y-4">
              <fieldset class="flex flex-col gap-1">
                <Label for="companyName"> Company Name </Label>
                <Input
                  v-bind="companyNameAttrs"
                  id="companyName"
                  v-model="companyName"
                />
                <span class="text-error-400">{{ errors?.companyName }}</span>
              </fieldset>
              <fieldset class="flex flex-col gap-1">
                <Label for="address"> Address </Label>
                <Input v-bind="addressAttrs" id="address" v-model="address" />
                <span class="text-error-400">{{ errors?.address }}</span>
              </fieldset>
              <div class="flex gap-8">
                <div class="w-1/2">
                  <fieldset class="flex flex-col gap-1">
                    <Label for="city"> City </Label>
                    <Input v-bind="cityAttrs" id="city" v-model="city" />
                    <span class="text-error-400">{{ errors?.city }}</span>
                  </fieldset>
                </div>
                <div class="w-1/2">
                  <fieldset class="flex flex-col gap-1">
                    <Label for="state"> State </Label>
                    <Input v-bind="stateAttrs" id="state" v-model="state" />
                    <span class="text-error-400">{{ errors?.state }}</span>
                  </fieldset>
                </div>
              </div>
              <div class="flex gap-8">
                <div class="w-1/2">
                  <fieldset class="flex flex-col gap-1">
                    <Label for="zip"> Zip / Postal Code </Label>
                    <Input v-bind="zipAttrs" id="zip" v-model="zip" />
                    <span class="text-error-400">{{ errors?.zip }}</span>
                  </fieldset>
                </div>
                <div class="w-1/2">
                  <fieldset class="flex flex-col gap-1">
                    <Label for="vatId"> VAT ID </Label>
                    <Input v-bind="vatIdAttrs" id="vatId" v-model="vatId" />
                    <span class="text-error-400">{{ errors?.vatId }}</span>
                  </fieldset>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </form>
      </div>
      <div>
        <Sidebar
          :selected-package="selectedPackage"
          :is-pending="isPaymentPending"
          @on-apply-coupon="applyCoupon"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { loadStripe } from '@stripe/stripe-js';
import { ChevronDown, ChevronUp } from 'lucide-vue-next';
import { toast } from 'vue-sonner';

// TODO: Create a new component for the radio group inside ui/components
import { RadioGroupIndicator, RadioGroupItem, RadioGroupRoot } from 'radix-vue';

import requests from '@vuex/requests.js';

import { Input } from '@ui/components/input';
import { Label } from '@ui/components/label';
import { Button } from '@ui/components/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@ui/components/collapsible';

import Sidebar from './Sidebar.vue';

const isPaymentPending = ref(false);
const store = useStore();
const countries = computed(() => store.getters['settings/getCountries']);

const queryClient = useQueryClient();
const isBillingDetailsOpen = ref(false);
const selectedPackage = ref();
const selectedPriceId = ref('');
const trxIntent = ref();

const props = defineProps({
  selectedProduct: {
    type: Object,
    default: null,
  },
});

const emits = defineEmits(['successPayment']);

const prices = { ...props.selectedProduct }.prices.sort(
  (a, b) => a.yearly_unit_amount - b.yearly_unit_amount
);
[selectedPackage.value] = prices;
selectedPriceId.value = prices[0].stripe_price_id;

watch(selectedPriceId, async next => {
  [selectedPackage.value] = prices.filter(
    price => price.stripe_price_id === next
  );
});

const validationSchema = toTypedSchema(
  z.object({
    cardName: z
      .string()
      .min(1, {
        message: 'The card name field is required',
      })
      .max(255),
    companyName: z.string().max(255),
    address: z.string().max(255),
    city: z.string().max(255),
    state: z.string().max(255),
    zip: z.string().max(255),
    vatId: z.string().max(255),
    coupon: z.string().max(255),
    country: z.string().refine(country => country.length > 0, {
      message: 'Country is required',
    }),
  })
);

const { handleSubmit, errors, defineField, setValues, setFieldError } = useForm(
  {
    validationSchema,
    initialValues: {
      cardName: '',
      companyName: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      vatId: '',
      coupon: '',
      country: '',
    },
  }
);

let stripe;
let elements;
let paymentElement;

const initStripe = async ({ intent }) => {
  stripe = await loadStripe(process.env.MIX_STRIPE_KEY);
  elements = stripe.elements({
    clientSecret: intent,

    appearance: {
      disableAnimations: false,
      variables: {
        colorPrimary: '#ffffff',
        colorBackground: '#1a1e1e',
        colorText: '#ffffff',
        colorDanger: '#FF5449',
        fontFamily: 'Open Sans, sans-serif',
        spacingUnit: '2px',
        borderRadius: '4px',
        gridRowSpacing: '32px',
        gridColumnSpacing: '32px',
        // See all possible variables below
      },
      rules: {
        '.Label': {
          padding: '0',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: '4px',
        },
        '.Input': {
          height: '40px',
          padding: '10px 12px',
          backgroundColor: '#2F3637',
          borderColor: '#5E6B6E',
        },
        '.Error': {
          marginTop: '4px',
        },
      },
    },
  });

  paymentElement = elements.create('payment', {
    terms: {
      card: 'never',
    },
    layout: 'auto',
    fields: {
      billingDetails: {
        address: {
          country: 'never',
        },
      },
    },
  });
  paymentElement.mount('#payment-element');
};

onMounted(async () => {
  try {
    const intentRes = await requests.workspaces.subscription.getIntent(
      store.getters['workspace/getId']
    );
    trxIntent.value = intentRes.intent;

    initStripe({ intent: trxIntent.value });
  } catch (error) {
    console.log(error);
  }
});

const refreshIntent = async () => {
  try {
    const intentRes = await requests.workspaces.subscription.getIntent(
      store.getters['workspace/getId']
    );
    trxIntent.value = intentRes.intent;

    initStripe({ intent: trxIntent.value });
  } catch (error) {
    console.log(error);
  }
};

const [cardName, cardNameAttrs] = defineField('cardName');
const [companyName, companyNameAttrs] = defineField('companyName');
const [address, addressAttrs] = defineField('address');
const [city, cityAttrs] = defineField('city');
const [state, stateAttrs] = defineField('state');
const [zip, zipAttrs] = defineField('zip');
const [vatId, vatIdAttrs] = defineField('vatId');
const [country, countryAttrs] = defineField('country');

const { mutate } = useMutation({
  mutationFn: async payload => {
    const res = await requests.workspaces.subscription.create(
      store.getters['workspace/getId'],
      { ...payload }
    );
    store.commit('workspace/setWorkspace', { value: res.data });
    emits('successPayment');
    await queryClient.invalidateQueries();
  },
  onSuccess: async () => {
    isPaymentPending.value = false;
    toast.success('Your payment has been processed successfully');
  },
  onError: async error => {
    isPaymentPending.value = false;
    toast.error(
      error?.response?.data?.title ||
        'Something went wrong, please try again later'
    );
    await refreshIntent();
  },
});

const onSubmit = handleSubmit(async values => {
  isPaymentPending.value = true;
  // TODO:: Find a better way to do this
  // maybe useMutation
  if (values.vatId) {
    const validateVatRes = await requests.workspaces.subscription.validateVat(
      store.getters['workspace/getId'],
      {
        vat: values.vatId,
      }
    );

    if (validateVatRes.response?.status === 400) {
      isPaymentPending.value = false;
      setFieldError(
        'vatId',
        validateVatRes?.data?.detail || 'Your VAT number is not correct'
      );

      return;
    }
  }

  const { error, setupIntent } = await stripe.confirmSetup({
    elements,
    confirmParams: {
      payment_method_data: {
        billing_details: {
          name: values.cardName,
          address: {
            country: values.country,
          },
        },
      },
    },
    redirect: 'if_required',
  });

  if (error) {
    isPaymentPending.value = false;
    toast.error(
      error?.message || 'Something went wrong, please try again later'
    );

    return;
  }

  const stripePaymentMethod = setupIntent.payment_method;

  const paymentData = {
    selectedPriceId: selectedPriceId.value,
    stripePaymentMethod,
    billingDetails: {
      companyName: values.companyName,
      address: values.address,
      city: values.city,
      state: values.state,
      zip: values.zip,
      vatId: values.vatId,
      country: values.country,
    },
    coupon: values.coupon,
  };

  mutate(paymentData);
});

// Apply coupon
const applyCoupon = ({ promoCode }) => {
  setValues({
    coupon: promoCode,
  });
};
</script>
